import React, { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { ExperienceItem } from "@/hooks/useProfile";
import EmptyState from "./EmptyState";
import ExperienceItemCard from "./experience/ExperienceItemCard";
import AddExperienceForm from "./experience/AddExperienceForm";
import { useExperienceSection } from "./experience/useExperienceSection";
import { User } from "@supabase/supabase-js";
import { useFormPersistence } from "@/hooks/dashboard/useFormPersistence";
import { usePersistentFormState } from "@/hooks/dashboard/usePersistentFormState";

interface ExperienceSectionProps {
  experienceItems: ExperienceItem[] | null;
  onExperienceChange: (experienceItems: ExperienceItem[]) => void;
  user?: User;
  skills?: string[] | null;
  activeSection?: string;
}

const ExperienceSection = ({
  experienceItems = [],
  onExperienceChange,
  user,
  skills = [],
  activeSection = "experience",
}: ExperienceSectionProps) => {
  // Use form persistence hook
  useFormPersistence(activeSection);

  const {
    isAdding,
    isSaving,
    newlyAddedIds,
    setIsAdding,
    handleAddItem,
    handleRemoveItem,
    handleAddFirstExperience,
    resetState,
  } = useExperienceSection(
    experienceItems,
    onExperienceChange,
    user?.id,
    activeSection
  );

  // Use persistent form state for editing experience items
  const [editState, setEditState, resetEditState] = usePersistentFormState<{
    item: ExperienceItem | null;
    index: number | null;
  }>({ item: null, index: null }, "experience_edit_state", activeSection);

  const handleEditItem = useCallback(
    (item: ExperienceItem, index: number) => {
      setEditState({ item, index });
    },
    [setEditState]
  );

  const handleUpdateItem = useCallback(
    (updatedItem: ExperienceItem) => {
      if (!editState.item || editState.index === null) return;

      const updatedItems = [...(experienceItems || [])];
      updatedItems[editState.index] = {
        ...updatedItem,
        id: editState.item.id, // Preserve the original ID
      };
      console.log("updating");

      onExperienceChange(updatedItems);
      resetEditState();
    },
    [editState, experienceItems, onExperienceChange, resetEditState]
  );

  const handleCancelEdit = useCallback(() => {
    resetEditState();
  }, [resetEditState]);

  const handleSuccessfulAdd = useCallback(() => {
    resetState();
  }, [resetState]);

  const hasItems = experienceItems && experienceItems.length > 0;
  const isEditing = editState.item !== null && editState.index !== null;
  const showAddButton = !isAdding && !isEditing && hasItems;

  return (
    <div className="space-y-4">

      <div className="flex md:flex-row flex-col justify-between gap-4 md:items-center mb-8">
        <h1 className="text-base font-medium">Professional Experience</h1>
        {/* <Label className="text-lg font-medium">Professional Experience</Label> */}
        {showAddButton && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-1"
            disabled={isSaving}
          >
            <Plus className="h-4 w-4" />
            Add Experience
          </Button>
        )}
      </div>

      {/* Experience Items List */}
      {hasItems ? (
        <div className="space-y-4">
          {experienceItems.map((item, index) => (
            <ExperienceItemCard
              key={item.id || index}
              item={item}
              index={index}
              onRemove={handleRemoveItem}
              onEdit={!isEditing && !isAdding ? handleEditItem : undefined}
              isNewlyAdded={item.id && newlyAddedIds.includes(item.id)}
              disabled={isSaving}
            />
          ))}
        </div>
      ) : !isAdding && !isEditing ? (
        <EmptyState type="experience" onAddClick={handleAddFirstExperience} />
      ) : null}

      {/* Add/Edit Experience Form */}
      {(isAdding || isEditing) && (
        <AddExperienceForm
          onSave={
            isEditing
              ? handleUpdateItem
              : (item) => {
                  handleAddItem(item);
                  handleSuccessfulAdd();
                }
          }
          onCancel={
            isEditing
              ? handleCancelEdit
              : () => {
                  setIsAdding(false);
                  resetState();
                }
          }
          disabled={isSaving}
          initialData={isEditing ? editState.item : undefined}
          isEditing={isEditing}
        />
      )}
    </div>
  );
};

export default ExperienceSection;
